import type {
  AICheckConfig,
  AICheckResult,
  AICheckRunCheckpoint,
  AICheckRunResult,
  CheckLane,
  ConsistencyCheckResult,
  ConsistencyRuleConfig,
  ContactTopicsResult,
  CustomerProfileResult,
  OutputLanguage,
  PipelineGraphSkeleton,
  PipelineNodeMembers,
  PipelineNodeRuntimeStatus,
} from "@/types/aicheck";
import type { ActiveRunRuntimeSnapshot } from "./run-runtime-registry";

const NODE_IDS = {
  FETCH: "sys-fetch",
  CONSISTENCY_INDEPENDENT: "consistency-independent",
  COMPACTION: "sys-compaction",
  PROFILE: "sys-profile",
  CONSISTENCY_PROFILE: "consistency-profile",
} as const;

export interface AdminRerunSnapshot {
  source: "server_cache" | "browser_checkpoint" | "final_result_cache";
  projectId: number;
  language: OutputLanguage;
  projectData: AICheckRunResult["projectData"];
  aiResults: AICheckResult[];
  consistencyResults: ConsistencyCheckResult[];
  customerProfileResult: CustomerProfileResult | null;
  contactTopicsResult: ContactTopicsResult | null;
  plannedCheckIds: string[];
  plannedCheckNameById: Record<string, string>;
  checkLaneMap: Record<string, CheckLane>;
  checkBatchGroups: Array<{ id: string; name: string; checks: string[] }>;
  graphSkeleton: PipelineGraphSkeleton;
  nodeMembers: PipelineNodeMembers;
  independentConsistencyRules?: Array<{ name: string; passed: boolean }>;
  profileConsistencyRules?: Array<{ name: string; passed: boolean }>;
}

export interface AdminRerunPlan {
  previousResult: AICheckRunResult | null;
  previousContactTopics: ContactTopicsResult | null;
  rerunCheckIds: string[];
  invalidatedNodeIds: string[];
  preservedNodeIds: string[];
  skipCompaction: boolean;
  skipProfile: boolean;
  reevaluateConsistency: true;
  projectDataOverride: AICheckRunResult["projectData"];
}

function hasSnapshotMetadata(
  snapshot: Partial<AdminRerunSnapshot>,
): snapshot is AdminRerunSnapshot {
  return Boolean(
    snapshot.projectData &&
      snapshot.plannedCheckIds &&
      snapshot.plannedCheckNameById &&
      snapshot.checkLaneMap &&
      snapshot.checkBatchGroups &&
      snapshot.graphSkeleton &&
      snapshot.nodeMembers,
  );
}

function toSnapshot(
  source: AdminRerunSnapshot["source"],
  input: Partial<AdminRerunSnapshot>,
): AdminRerunSnapshot | null {
  if (!hasSnapshotMetadata(input)) return null;
  return {
    source,
    projectId: input.projectId ?? 0,
    language: input.language ?? "en",
    projectData: input.projectData,
    aiResults: [...(input.aiResults ?? [])],
    consistencyResults: [...(input.consistencyResults ?? [])],
    customerProfileResult: input.customerProfileResult ?? null,
    contactTopicsResult: input.contactTopicsResult ?? null,
    plannedCheckIds: [...input.plannedCheckIds],
    plannedCheckNameById: { ...input.plannedCheckNameById },
    checkLaneMap: { ...input.checkLaneMap },
    checkBatchGroups: [...input.checkBatchGroups],
    graphSkeleton: structuredClone(input.graphSkeleton),
    nodeMembers: structuredClone(input.nodeMembers),
    independentConsistencyRules: input.independentConsistencyRules
      ? [...input.independentConsistencyRules]
      : undefined,
    profileConsistencyRules: input.profileConsistencyRules
      ? [...input.profileConsistencyRules]
      : undefined,
  };
}

function buildFallbackProjectData(
  timestamp: string,
): AICheckRunResult["projectData"] {
  return {
    project: {} as AICheckRunResult["projectData"]["project"],
    itineraries: [],
    arrangements: [],
    paymentSummary: null,
    profitSummary: null,
    fetchedAt: timestamp,
  };
}

function sumTokens(
  aiResults: AICheckResult[],
  customerProfileResult: CustomerProfileResult | null,
  contactTopicsResult: ContactTopicsResult | null,
): { prompt: number; completion: number; total: number } {
  const totals = { prompt: 0, completion: 0, total: 0 };
  for (const result of aiResults) {
    totals.prompt += result.tokensUsed?.prompt ?? 0;
    totals.completion += result.tokensUsed?.completion ?? 0;
    totals.total += result.tokensUsed?.total ?? 0;
  }
  if (customerProfileResult?.tokensUsed) {
    totals.prompt += customerProfileResult.tokensUsed.prompt ?? 0;
    totals.completion += customerProfileResult.tokensUsed.completion ?? 0;
    totals.total += customerProfileResult.tokensUsed.total ?? 0;
  }
  if (contactTopicsResult?.tokensUsed) {
    totals.prompt += contactTopicsResult.tokensUsed.prompt ?? 0;
    totals.completion += contactTopicsResult.tokensUsed.completion ?? 0;
    totals.total += contactTopicsResult.tokensUsed.total ?? 0;
  }
  return totals;
}

function deriveConsistencyRuleSummaries(
  consistencyResults: ConsistencyCheckResult[],
  nodeMembers: PipelineNodeMembers | undefined,
): Pick<
  AICheckRunResult,
  "independentConsistencyRules" | "profileConsistencyRules"
> {
  const independentRuleIds = new Set(
    nodeMembers?.[NODE_IDS.CONSISTENCY_INDEPENDENT]?.ruleIds ?? [],
  );
  const profileRuleIds = new Set(
    nodeMembers?.[NODE_IDS.CONSISTENCY_PROFILE]?.ruleIds ?? [],
  );

  return {
    independentConsistencyRules: consistencyResults
      .filter((result) => independentRuleIds.has(result.ruleId))
      .map((result) => ({
        name: result.ruleName,
        passed: result.passed,
      })),
    profileConsistencyRules: consistencyResults
      .filter((result) => profileRuleIds.has(result.ruleId))
      .map((result) => ({
        name: result.ruleName,
        passed: result.passed,
      })),
  };
}

function toRunResult(
  input: {
    projectId: number;
    language: OutputLanguage;
    projectData?: AICheckRunResult["projectData"];
    aiResults: AICheckResult[];
    consistencyResults: ConsistencyCheckResult[];
    customerProfileResult: CustomerProfileResult | null;
    contactTopicsResult: ContactTopicsResult | null;
    plannedCheckIds?: string[];
    plannedCheckNameById?: Record<string, string>;
    checkLaneMap?: Record<string, CheckLane>;
    checkBatchGroups?: Array<{ id: string; name: string; checks: string[] }>;
    graphSkeleton?: PipelineGraphSkeleton;
    nodeMembers?: PipelineNodeMembers;
    projectDataSource?: AICheckRunResult["projectDataSource"];
    independentConsistencyRules?: Array<{ name: string; passed: boolean }>;
    profileConsistencyRules?: Array<{ name: string; passed: boolean }>;
  },
  params: {
    startedAt: string;
    completedAt?: string;
    status: AICheckRunResult["status"];
    error?: string;
  },
): AICheckRunResult {
  const plannedCheckIds = input.plannedCheckIds ?? [];
  const plannedCheckNameById = input.plannedCheckNameById ?? {};
  const derivedConsistencyRuleSummaries = deriveConsistencyRuleSummaries(
    input.consistencyResults,
    input.nodeMembers,
  );

  return {
    projectId: input.projectId,
    startedAt: params.startedAt,
    completedAt: params.completedAt ?? new Date().toISOString(),
    status: params.status,
    language: input.language,
    projectData:
      input.projectData ?? buildFallbackProjectData(params.startedAt),
    projectDataSource: input.projectDataSource,
    aiResults: [...input.aiResults],
    consistencyResults: [...input.consistencyResults],
    customerProfileResult: input.customerProfileResult,
    contactTopicsResult: input.contactTopicsResult,
    totalTokensUsed: sumTokens(
      input.aiResults,
      input.customerProfileResult,
      input.contactTopicsResult,
    ),
    ...(params.error ? { error: params.error } : undefined),
    checkLaneMap: input.checkLaneMap,
    plannedChecks: plannedCheckIds.map(
      (checkId) => plannedCheckNameById[checkId] ?? checkId,
    ),
    plannedCheckIds,
    plannedCheckNameById,
    graphSkeleton: input.graphSkeleton,
    nodeMembers: input.nodeMembers,
    checkBatchGroups: input.checkBatchGroups,
    independentConsistencyRules:
      input.independentConsistencyRules ??
      derivedConsistencyRuleSummaries.independentConsistencyRules,
    profileConsistencyRules:
      input.profileConsistencyRules ??
      derivedConsistencyRuleSummaries.profileConsistencyRules,
  };
}

export function buildRunResultFromSnapshot(
  snapshot: AdminRerunSnapshot,
  params: {
    startedAt: string;
    completedAt?: string;
    status: AICheckRunResult["status"];
    error?: string;
  },
): AICheckRunResult {
  return toRunResult(snapshot, params);
}

export function buildRunResultFromCheckpoint(
  checkpoint: AICheckRunCheckpoint,
  params: {
    startedAt: string;
    completedAt?: string;
    status: AICheckRunResult["status"];
    error?: string;
  },
): AICheckRunResult {
  return toRunResult(
    {
      projectId: checkpoint.projectId,
      language: checkpoint.language,
      projectData: checkpoint.projectDataSnapshot,
      aiResults: checkpoint.aiResults.map((entry) => entry.result),
      consistencyResults: checkpoint.consistencyResults,
      customerProfileResult: checkpoint.customerProfileResult?.data ?? null,
      contactTopicsResult: checkpoint.contactTopicsResult?.data ?? null,
      plannedCheckIds: checkpoint.plannedCheckIds,
      plannedCheckNameById: checkpoint.plannedCheckNameById,
      checkLaneMap: checkpoint.checkLaneMap,
      checkBatchGroups: checkpoint.checkBatchGroups,
      graphSkeleton: checkpoint.graphSkeleton,
      nodeMembers: checkpoint.nodeMembers,
      projectDataSource: checkpoint.projectDataSource,
    },
    params,
  );
}

export function buildNodeMembers(params: {
  independentRules: ConsistencyRuleConfig[];
  profileDependentRules: ConsistencyRuleConfig[];
  independentChecks: AICheckConfig[];
  dependentChecks: AICheckConfig[];
  afterAllChecks: AICheckConfig[];
  checkBatchGroups: Array<{ id: string; name: string; checks: string[] }>;
}): PipelineNodeMembers {
  const {
    independentRules,
    profileDependentRules,
    independentChecks,
    dependentChecks,
    afterAllChecks,
    checkBatchGroups,
  } = params;

  const members: PipelineNodeMembers = {
    [NODE_IDS.FETCH]: { kind: "system" },
    [NODE_IDS.CONSISTENCY_INDEPENDENT]: {
      kind: "consistency",
      ruleIds: independentRules.map((rule) => rule.id),
    },
    [NODE_IDS.COMPACTION]: { kind: "system" },
    [NODE_IDS.PROFILE]: { kind: "system" },
    [NODE_IDS.CONSISTENCY_PROFILE]: {
      kind: "consistency",
      ruleIds: profileDependentRules.map((rule) => rule.id),
    },
  };

  const groupedChecks = new Set(
    checkBatchGroups.flatMap((group) => group.checks.map((name) => name)),
  );
  const allChecks = [...independentChecks, ...dependentChecks];

  for (const group of checkBatchGroups) {
    const memberIds = allChecks
      .filter((check) => group.checks.includes(check.name))
      .map((check) => check.id);
    if (memberIds.length === 0) continue;
    members[`ai-batch-${group.id}`] = {
      kind: "ai-check",
      checkIds: memberIds,
    };
  }

  for (const check of allChecks) {
    if (groupedChecks.has(check.name)) continue;
    members[`ai-${check.name}`] = {
      kind: "ai-check",
      checkIds: [check.id],
    };
  }

  for (const check of afterAllChecks) {
    members[`summary-${check.name}`] = {
      kind: "summary",
      checkIds: [check.id],
    };
  }

  return members;
}

export function collectDescendantNodeIds(
  graphSkeleton: PipelineGraphSkeleton,
  targetNodeId: string,
): string[] {
  const adjacency = new Map<string, string[]>();
  for (const edge of graphSkeleton.edges) {
    const current = adjacency.get(edge.source) ?? [];
    current.push(edge.target);
    adjacency.set(edge.source, current);
  }
  const visited = new Set<string>();
  const queue = [targetNodeId];
  while (queue.length > 0) {
    const nodeId = queue.shift();
    if (!nodeId || visited.has(nodeId)) continue;
    visited.add(nodeId);
    for (const next of adjacency.get(nodeId) ?? []) {
      queue.push(next);
    }
  }
  return [...visited];
}

export function buildSnapshotFromCheckpoint(
  checkpoint: AICheckRunCheckpoint,
): AdminRerunSnapshot | null {
  return toSnapshot("browser_checkpoint", {
    projectId: checkpoint.projectId,
    language: checkpoint.language,
    projectData: checkpoint.projectDataSnapshot,
    aiResults: checkpoint.aiResults.map((entry) => entry.result),
    consistencyResults: checkpoint.consistencyResults,
    customerProfileResult: checkpoint.customerProfileResult?.data ?? null,
    contactTopicsResult: checkpoint.contactTopicsResult?.data ?? null,
    plannedCheckIds: checkpoint.plannedCheckIds,
    plannedCheckNameById: checkpoint.plannedCheckNameById,
    checkLaneMap: checkpoint.checkLaneMap,
    checkBatchGroups: checkpoint.checkBatchGroups,
    graphSkeleton: checkpoint.graphSkeleton,
    nodeMembers: checkpoint.nodeMembers,
  });
}

export function buildSnapshotFromResult(
  result: AICheckRunResult,
  source: "server_cache" | "final_result_cache",
): AdminRerunSnapshot | null {
  return toSnapshot(source, {
    projectId: result.projectId,
    language: result.language,
    projectData: result.projectData,
    aiResults: result.aiResults,
    consistencyResults: result.consistencyResults,
    customerProfileResult: result.customerProfileResult,
    contactTopicsResult: result.contactTopicsResult ?? null,
    plannedCheckIds: result.plannedCheckIds,
    plannedCheckNameById: result.plannedCheckNameById,
    checkLaneMap: result.checkLaneMap,
    checkBatchGroups: result.checkBatchGroups,
    graphSkeleton: result.graphSkeleton,
    nodeMembers: result.nodeMembers,
    independentConsistencyRules: result.independentConsistencyRules,
    profileConsistencyRules: result.profileConsistencyRules,
  });
}

export function buildSnapshotFromActiveRun(
  snapshot: ActiveRunRuntimeSnapshot,
): AdminRerunSnapshot | null {
  return toSnapshot("server_cache", {
    projectId: snapshot.projectId,
    language: snapshot.language,
    projectData: snapshot.projectData,
    aiResults: snapshot.aiResults,
    consistencyResults: snapshot.consistencyResults,
    customerProfileResult: snapshot.customerProfileResult ?? null,
    contactTopicsResult: snapshot.contactTopicsResult ?? null,
    plannedCheckIds: snapshot.plannedCheckIds,
    plannedCheckNameById: snapshot.plannedCheckNameById,
    checkLaneMap: snapshot.checkLaneMap,
    checkBatchGroups: snapshot.checkBatchGroups,
    graphSkeleton: snapshot.graphSkeleton,
    nodeMembers: snapshot.nodeMembers,
    independentConsistencyRules: snapshot.independentConsistencyRules,
    profileConsistencyRules: snapshot.profileConsistencyRules,
  });
}

export function selectPreferredAdminRerunSnapshot(options: {
  activeSnapshot?: AdminRerunSnapshot | null;
  browserCheckpointSnapshot?: AdminRerunSnapshot | null;
  serverFinalSnapshot?: AdminRerunSnapshot | null;
  clientFinalSnapshot?: AdminRerunSnapshot | null;
}): AdminRerunSnapshot | null {
  return (
    options.activeSnapshot ??
    options.browserCheckpointSnapshot ??
    options.serverFinalSnapshot ??
    options.clientFinalSnapshot ??
    null
  );
}

function buildCheckpointNodeStates(
  snapshot: AdminRerunSnapshot,
  plan: AdminRerunPlan,
): Record<string, PipelineNodeRuntimeStatus> {
  const invalidatedNodeIds = new Set(plan.invalidatedNodeIds);
  const preservedNodeIds = new Set(plan.preservedNodeIds);
  const next: Record<string, PipelineNodeRuntimeStatus> = {};

  for (const node of snapshot.graphSkeleton.nodes) {
    if (invalidatedNodeIds.has(node.id)) {
      next[node.id] = "planned";
      continue;
    }
    if (preservedNodeIds.has(node.id)) {
      next[node.id] = "completed";
      continue;
    }
    next[node.id] = "planned";
  }

  return next;
}

export function buildCheckpointSeedFromAdminRerunPlan(
  snapshot: AdminRerunSnapshot,
  plan: AdminRerunPlan,
  startedAt: string,
): AICheckRunCheckpoint {
  const preservedResult = plan.previousResult;

  return {
    projectId: snapshot.projectId,
    language: snapshot.language,
    startedAt,
    updatedAt: startedAt,
    projectDataSnapshot: plan.projectDataOverride,
    plannedCheckIds: snapshot.plannedCheckIds,
    plannedCheckNameById: snapshot.plannedCheckNameById,
    checkLaneMap: snapshot.checkLaneMap,
    checkBatchGroups: snapshot.checkBatchGroups,
    graphSkeleton: snapshot.graphSkeleton,
    nodeMembers: snapshot.nodeMembers,
    completedCheckIds:
      preservedResult?.aiResults.map((result) => result.checkId) ?? [],
    aiResults:
      preservedResult?.aiResults.map((result) => ({
        result,
      })) ?? [],
    consistencyResults: preservedResult?.consistencyResults ?? [],
    customerProfileResult:
      preservedResult?.customerProfileResult == null
        ? undefined
        : {
            data: preservedResult.customerProfileResult,
          },
    contactTopicsResult:
      plan.previousContactTopics == null
        ? undefined
        : {
            data: plan.previousContactTopics,
          },
    nodeStates: buildCheckpointNodeStates(snapshot, plan),
    nodeExecutionDirectives: [],
    nodeControlVersion: 0,
    lastKnownStatus: "reconnecting",
  };
}

export function planAdminRerunFromSnapshot(
  snapshot: AdminRerunSnapshot,
  targetNodeId: string,
): AdminRerunPlan {
  const invalidatedNodeIds = collectDescendantNodeIds(
    snapshot.graphSkeleton,
    targetNodeId,
  );
  const invalidatedNodeIdSet = new Set(invalidatedNodeIds);
  const invalidatedCheckIds = new Set<string>();
  const invalidatedRuleIds = new Set<string>();

  for (const nodeId of invalidatedNodeIds) {
    const members = snapshot.nodeMembers[nodeId];
    if (!members) continue;
    for (const checkId of members.checkIds ?? []) {
      invalidatedCheckIds.add(checkId);
    }
    for (const ruleId of members.ruleIds ?? []) {
      invalidatedRuleIds.add(ruleId);
    }
  }

  const preservedNodeIds = snapshot.graphSkeleton.nodes
    .map((node) => node.id)
    .filter((nodeId) => !invalidatedNodeIdSet.has(nodeId));

  const preservedAIResults = snapshot.aiResults.filter(
    (result) => !invalidatedCheckIds.has(result.checkId),
  );
  const preservedConsistencyResults = snapshot.consistencyResults.filter(
    (result) => !invalidatedRuleIds.has(result.ruleId),
  );

  const preserveCompaction =
    !invalidatedNodeIdSet.has(NODE_IDS.FETCH) &&
    !invalidatedNodeIdSet.has(NODE_IDS.COMPACTION);
  const preserveProfile =
    preserveCompaction && !invalidatedNodeIdSet.has(NODE_IDS.PROFILE);

  const previousResult =
    preservedAIResults.length > 0 ||
    preservedConsistencyResults.length > 0 ||
    (preserveProfile && snapshot.customerProfileResult) ||
    (preserveCompaction && snapshot.contactTopicsResult)
      ? {
          projectId: snapshot.projectId,
          startedAt: "",
          completedAt: "",
          status: "completed" as const,
          language: snapshot.language,
          projectData: snapshot.projectData,
          aiResults: preservedAIResults,
          consistencyResults: preservedConsistencyResults,
          customerProfileResult: preserveProfile
            ? snapshot.customerProfileResult
            : null,
          contactTopicsResult: preserveCompaction
            ? snapshot.contactTopicsResult
            : null,
          totalTokensUsed: { prompt: 0, completion: 0, total: 0 },
          checkLaneMap: snapshot.checkLaneMap,
          plannedChecks: snapshot.plannedCheckIds.map(
            (checkId) => snapshot.plannedCheckNameById[checkId] ?? checkId,
          ),
          plannedCheckIds: snapshot.plannedCheckIds,
          plannedCheckNameById: snapshot.plannedCheckNameById,
          graphSkeleton: snapshot.graphSkeleton,
          nodeMembers: snapshot.nodeMembers,
          checkBatchGroups: snapshot.checkBatchGroups,
          independentConsistencyRules: snapshot.independentConsistencyRules,
          profileConsistencyRules: snapshot.profileConsistencyRules,
        }
      : null;

  return {
    previousResult,
    previousContactTopics: preserveCompaction
      ? snapshot.contactTopicsResult
      : null,
    rerunCheckIds: snapshot.plannedCheckIds.filter((checkId) =>
      invalidatedCheckIds.has(checkId),
    ),
    invalidatedNodeIds,
    preservedNodeIds,
    skipCompaction: preserveCompaction,
    skipProfile: preserveProfile,
    reevaluateConsistency: true,
    projectDataOverride: snapshot.projectData,
  };
}
